import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { useAdminOrg, useCreateOrgUser, useCreateProject, useUserAction } from "@/api/hooks/admin";
import { errorMessage, fieldError } from "@/api/errors";
import type { AdminOrgUser, ProjectStatus } from "@/api/types";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EmptyState } from "@/components/EmptyState";
import { ErrorNotice } from "@/components/ErrorNotice";
import { Field, FormError } from "@/components/Field";
import { PageHeader } from "@/components/PageHeader";
import { PageLoading } from "@/components/Spinner";
import { StatusPill } from "@/components/StatusPill";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { usePageCrumbs } from "@/app/layouts/shellContext";

export const PROJECT_STATUSES: ProjectStatus[] = ["requested", "discovery", "design", "build", "live", "paused", "closed"];

const userSchema = z.object({
  name: z.string().trim().min(1, "members.nameRequired"),
  email: z.string().trim().min(1, "members.emailRequired").email("members.emailInvalid"),
  role: z.enum(["owner", "member"]),
});
type UserValues = z.infer<typeof userSchema>;

function AddUserDialog({ orgId, open, onOpenChange }: { orgId: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const create = useCreateOrgUser(orgId);
  const form = useForm<UserValues>({ resolver: zodResolver(userSchema), defaultValues: { name: "", email: "", role: "member" } });
  const { errors } = form.formState;
  const serverName = fieldError(create.error, "name");
  const serverEmail = fieldError(create.error, "email");
  const onSubmit = form.handleSubmit((v) =>
    create.mutate(v, {
      onSuccess: () => {
        toast.success(t("members.inviteSent", { email: v.email }));
        form.reset();
        onOpenChange(false);
      },
    }),
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("admin.org.addUser")}</DialogTitle>
          <DialogDescription>{t("admin.org.addUserIntro")}</DialogDescription>
        </DialogHeader>
        <form noValidate onSubmit={onSubmit} className="flex flex-col gap-4">
          <FormError message={create.isError && !serverName && !serverEmail ? errorMessage(create.error, t("errors.generic")) : null} />
          <Field label={t("members.name")} error={errors.name?.message ? t(errors.name.message) : serverName}>
            {(p) => <Input {...p} {...form.register("name")} />}
          </Field>
          <Field label={t("members.email")} error={errors.email?.message ? t(errors.email.message) : serverEmail}>
            {(p) => <Input {...p} type="email" {...form.register("email")} />}
          </Field>
          <Field label={t("members.roleLabel")}>
            {(p) => (
              <Select {...p} className="w-full" {...form.register("role")}>
                <option value="member">{t("members.role.member")}</option>
                <option value="owner">{t("members.role.owner")}</option>
              </Select>
            )}
          </Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {t("members.sendInvite")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const projectSchema = z.object({
  name: z.string().trim().min(1, "admin.orgs.nameRequired"),
  status: z.enum(["discovery", "design", "build", "live", "paused", "closed"]),
});
type ProjectValues = z.infer<typeof projectSchema>;

function CreateProjectDialog({ orgId, open, onOpenChange }: { orgId: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateProject(orgId);
  const form = useForm<ProjectValues>({ resolver: zodResolver(projectSchema), defaultValues: { name: "", status: "discovery" } });
  const { errors } = form.formState;
  const onSubmit = form.handleSubmit((v) =>
    create.mutate(v, {
      onSuccess: (p) => {
        toast.success(t("admin.org.projectCreated"));
        onOpenChange(false);
        navigate(`/admin/projects/${p.id}`);
      },
    }),
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("admin.org.createProject")}</DialogTitle>
          <DialogDescription>{t("admin.org.createProjectIntro")}</DialogDescription>
        </DialogHeader>
        <form noValidate onSubmit={onSubmit} className="flex flex-col gap-4">
          <FormError message={create.isError ? errorMessage(create.error, t("errors.generic")) : null} />
          <Field label={t("admin.org.projectName")} error={errors.name?.message ? t(errors.name.message) : null}>
            {(p) => <Input {...p} {...form.register("name")} />}
          </Field>
          <Field label={t("admin.org.projectStatus")}>
            {(p) => (
              <Select {...p} className="w-full" {...form.register("status")}>
                {PROJECT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`project.status.${s}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {t("admin.org.createProject")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function OrgDetailPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const org = useAdminOrg(id);
  const action = useUserAction(id);
  usePageCrumbs([{ label: t("admin.nav.orgs"), to: "/admin" }, { label: org.data?.name ?? "" }]);
  const [addUser, setAddUser] = useState(false);
  const [addProject, setAddProject] = useState(false);
  const [toDeactivate, setToDeactivate] = useState<AdminOrgUser | null>(null);

  if (org.isPending) return <PageLoading />;
  if (org.isError) return <ErrorNotice error={org.error} />;
  const o = org.data;

  const run = (u: AdminOrgUser, kind: "resend-invite" | "reset-password" | "deactivate") =>
    action.mutate(
      { userId: u.id, action: kind },
      {
        onSuccess: () =>
          toast.success(
            kind === "resend-invite"
              ? t("admin.org.inviteResent", { email: u.email })
              : kind === "reset-password"
                ? t("admin.org.resetSent", { email: u.email })
                : t("admin.org.deactivated", { name: u.name }),
          ),
        onError: (e) => toast.error(errorMessage(e, t("errors.generic"))),
        onSettled: () => setToDeactivate(null),
      },
    );

  return (
    <div className="flex flex-col gap-8">
      <PageHeader title={o.name} description={<span className="font-mono text-sm">{o.slug}</span>} />

      <section aria-labelledby="people-title" className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="people-title">{t("admin.org.people")}</h2>
          <Button variant="secondary" onClick={() => setAddUser(true)}>
            {t("admin.org.addUser")}
          </Button>
        </div>
        {o.users.length === 0 ? (
          <EmptyState title={t("admin.org.noUsers")} />
        ) : (
          <Table aria-label={t("admin.org.people")}>
            <THead>
              <TR>
                <TH>{t("admin.org.colName")}</TH>
                <TH>{t("admin.org.colEmail")}</TH>
                <TH>{t("admin.org.colRole")}</TH>
                <TH>{t("admin.org.colStatus")}</TH>
                <TH>
                  <span className="sr-only">{t("admin.org.colActions")}</span>
                </TH>
              </TR>
            </THead>
            <TBody>
              {o.users.map((u) => (
                <TR key={u.id}>
                  <TD className="font-medium">{u.name}</TD>
                  <TD className="break-all text-fg-muted">{u.email}</TD>
                  <TD>{t(`members.role.${u.role}`)}</TD>
                  <TD>
                    {!u.is_active ? (
                      <Badge variant="outline">{t("admin.org.statusDeactivated")}</Badge>
                    ) : u.has_password ? (
                      <Badge>{t("admin.org.statusActive")}</Badge>
                    ) : (
                      <Badge variant="warning">{t("admin.org.statusInvited")}</Badge>
                    )}
                  </TD>
                  <TD>
                    <div className="flex flex-wrap justify-end gap-2">
                      {!u.has_password && u.is_active ? (
                        <Button size="sm" variant="ghost" onClick={() => run(u, "resend-invite")}>
                          {t("admin.org.resendInvite")}
                        </Button>
                      ) : null}
                      {u.is_active ? (
                        <Button size="sm" variant="ghost" onClick={() => run(u, "reset-password")}>
                          {t("admin.org.sendReset")}
                        </Button>
                      ) : null}
                      {u.is_active ? (
                        <Button size="sm" variant="ghost" onClick={() => setToDeactivate(u)}>
                          {t("admin.org.deactivate")}
                        </Button>
                      ) : null}
                      <Button size="sm" variant="link" asChild>
                        <Link to={`/admin/users/${u.id}/events`}>{t("admin.org.history")}</Link>
                      </Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </section>

      <section aria-labelledby="projects-title" className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="projects-title">{t("admin.org.projects")}</h2>
          <Button variant="secondary" onClick={() => setAddProject(true)}>
            {t("admin.org.createProject")}
          </Button>
        </div>
        {o.projects.length === 0 ? (
          <EmptyState title={t("admin.org.noProjects")} />
        ) : (
          <Table aria-label={t("admin.org.projects")}>
            <THead>
              <TR>
                <TH>{t("admin.org.colProject")}</TH>
                <TH>{t("admin.org.colProjectStatus")}</TH>
                <TH>{t("admin.org.colVersion")}</TH>
              </TR>
            </THead>
            <TBody>
              {o.projects.map((p) => (
                <TR key={p.id}>
                  <TD className="font-medium">
                    <span className="flex flex-wrap items-center gap-2">
                      <Link to={`/admin/projects/${p.id}`}>{p.name}</Link>
                      {p.status === "requested" ? <Badge variant="warning">{t("admin.newRequest")}</Badge> : null}
                    </span>
                  </TD>
                  <TD>
                    <StatusPill status={p.status} />
                  </TD>
                  <TD className="text-fg-muted">
                    {p.latest_version ? t("admin.org.versionN", { version: p.latest_version }) : t("admin.org.noVersion")}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </section>

      <AddUserDialog orgId={id} open={addUser} onOpenChange={setAddUser} />
      <CreateProjectDialog orgId={id} open={addProject} onOpenChange={setAddProject} />
      <ConfirmDialog
        open={toDeactivate !== null}
        onOpenChange={(o2) => !o2 && setToDeactivate(null)}
        title={t("admin.org.deactivateConfirm", { name: toDeactivate?.name ?? "" })}
        description={t("admin.org.deactivateBody")}
        confirmLabel={t("admin.org.deactivate")}
        busy={action.isPending}
        onConfirm={() => toDeactivate && run(toDeactivate, "deactivate")}
      />
    </div>
  );
}
